<template>
  <v-dialog
    :value="value"
    max-width="800"
    scrollable
    @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon
          left
          color="orange darken-1">
          mdi-shuffle-variant
        </v-icon>
        <div>
          <div class="text-h6 font-weight-bold">
            Rebalance suggestion
          </div>
          <div class="text-caption grey--text">
            Based on saved threshold settings
          </div>
        </div>
        <v-spacer />
        <v-btn
          icon
          @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />

      <v-card-text class="pa-5">
        <template v-if="loading">
          <v-row class="mb-4">
            <v-col
              v-for="n in 3"
              :key="n"
              cols="4">
              <v-card
                outlined
                class="pa-3"
                style="background: #fafafa;">
                <v-skeleton-loader
                  type="text"
                  class="mb-2" />
                <v-skeleton-loader
                  type="heading"
                  class="mb-2" />
                <v-skeleton-loader type="text" />
              </v-card>
            </v-col>
          </v-row>

          <v-skeleton-loader
            type="text"
            max-width="140"
            class="mb-3" />
          <v-card
            outlined
            class="pa-3 mb-3">
            <v-skeleton-loader type="list-item-two-line" />
          </v-card>
          <v-card
            outlined
            class="pa-3">
            <v-skeleton-loader type="list-item-two-line" />
          </v-card>
        </template>

        <template v-else>
          <v-row class="mb-4">
            <v-col cols="4">
              <v-card
                outlined
                class="pa-3 text-center"
                style="background: #fafafa;">
                <div class="text-caption grey--text font-weight-medium mb-1">
                  Overloaded factories
                </div>
                <div class="text-h5 font-weight-medium red--text">
                  {{ overloadedCount }}
                </div>
                <div class="text-caption grey--text">
                  above {{ settings.overloadThreshold }}%
                </div>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card
                outlined
                class="pa-3 text-center"
                style="background: #fafafa;">
                <div class="text-caption grey--text font-weight-medium mb-1">
                  Underused factories
                </div>
                <div class="text-h5 font-weight-medium blue--text">
                  {{ underusedCount }}
                </div>
                <div class="text-caption grey--text">
                  below {{ settings.underUsedThreshold }}%
                </div>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card
                outlined
                class="pa-3 text-center"
                style="background: #fafafa;">
                <div class="text-caption grey--text font-weight-medium mb-1">
                  Proposed moves
                </div>
                <div class="text-h5 font-weight-medium green--text">
                  {{ proposedMovesCount }}
                </div>
                <div class="text-caption grey--text">
                  products to reassign
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-alert
            v-if="suggestion.length === 0 && !settingsLoaded"
            type="info"
            outlined
            prominent
            class="mb-4">
            <div class="font-weight-medium mb-1">
              No settings configured
            </div>
            <div class="text-body-2">
              Configure rebalance settings below to see suggestions.
            </div>
          </v-alert>

          <v-alert
            v-if="suggestion.length === 0 && settingsLoaded"
            type="success"
            outlined
            prominent
            class="mb-4">
            <div class="font-weight-medium mb-1">
              All factories balanced
            </div>
            <div class="text-body-2">
              No factories exceed {{ settings.overloadThreshold }}% in the current window.
            </div>
          </v-alert>

          <div
            v-if="suggestion.length > 0"
            class="mb-4">
            <div class="text-caption grey--text text--darken-1 font-weight-bold text-uppercase mb-2">
              Factory Analysis
            </div>

            <v-card
              v-for="(entry, index) in suggestion"
              :key="entry.factoryId"
              outlined
              :class="['pa-3', index < suggestion.length - 1 ? 'mb-3' : '']">
              <div class="d-flex align-center justify-space-between mb-2">
                <span class="text-subtitle-2 font-weight-medium">{{ entry.factoryName }}</span>
                <v-chip
                  x-small
                  :color="entry.classification === 'Overloaded' ? 'red lighten-4' : 'amber lighten-4'">
                  <span :class="entry.classification === 'Overloaded' ? 'red--text text--darken-2' : 'amber--text text--darken-3'">
                    {{ entry.classification }}
                  </span>
                </v-chip>
              </div>

              <div
                class="d-flex align-center"
                style="gap: 24px;">
                <div>
                  <div class="text-caption grey--text">
                    Allocated / Capacity
                  </div>
                  <div class="text-body-2 font-weight-medium">
                    {{ entry.allocatedProduct }} / {{ entry.capacityProduct }} products
                  </div>
                </div>
                <div>
                  <div class="text-caption grey--text">
                    Utilization
                  </div>
                  <div
                    class="text-body-2 font-weight-medium"
                    :class="entry.classification === 'Overloaded' ? 'red--text' : 'blue--text'">
                    {{ entry.utilization }}%
                  </div>
                </div>
              </div>

              <template v-if="entry.classification === 'Overloaded'">
                <v-divider class="my-2" />
                <div class="text-caption grey--text font-weight-medium mb-1">
                  Suggested moves
                </div>
                <div v-if="entry.suggestions && entry.suggestions.length > 0">
                  <div
                    v-for="s in entry.suggestions"
                    :key="s.targetFactoryId"
                    class="d-flex align-center py-1"
                    style="gap: 6px;">
                    <v-icon
                      small
                      class="orange--text text--darken-1">
                      mdi-arrow-right
                    </v-icon>
                    <span class="text-body-2">
                      Move <strong>{{ s.moveQuantity }}</strong> products → {{ s.targetFactoryName }}
                    </span>
                  </div>
                </div>
                <div
                  v-else
                  class="text-caption grey--text font-italic">
                  No compatible underused factory found
                </div>
              </template>
            </v-card>
          </div>
        </template>

        <v-expansion-panels
          v-model="settingsExpanded"
          flat
          class="mb-2"
          style="background: #f8f9fb; border: 1px solid #eee; border-radius: 12px;">
          <v-expansion-panel style="background: transparent;">
            <v-expansion-panel-header>
              <div
                class="d-flex align-center"
                style="gap: 6px;">
                <v-icon small>
                  mdi-tune-variant
                </v-icon>
                <span class="text-body-2 font-weight-medium">Rebalance thresholds</span>
                <span class="text-caption grey--text ml-2">
                  overload > {{ settings.overloadThreshold }}% · underused &lt; {{ settings.underUsedThreshold }}%
                </span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-form ref="settingsForm">
                <v-row class="pt-2">
                  <v-col
                    cols="12"
                    md="4">
                    <div class="text-caption grey--text text--darken-1 mb-1 font-weight-medium">
                      Planning horizon (Month)
                    </div>
                    <v-btn-toggle
                      v-model="horizonMode"
                      mandatory
                      dense
                      color="primary"
                      style="width: 100%;">
                      <v-btn
                        small
                        :value="1"
                        class="text-none flex-grow-1">
                        1
                      </v-btn>
                      <v-btn
                        small
                        :value="3"
                        class="text-none flex-grow-1">
                        3
                      </v-btn>
                      <v-btn
                        small
                        :value="6"
                        class="text-none flex-grow-1">
                        6
                      </v-btn>
                      <v-btn
                        small
                        value="custom"
                        class="text-none flex-grow-1">
                        Custom
                      </v-btn>
                    </v-btn-toggle>
                    <v-text-field
                      v-if="horizonMode === 'custom'"
                      v-model.number="customHorizon"
                      type="number"
                      outlined
                      dense
                      hide-details
                      suffix="month"
                      min="1"
                      max="12"
                      class="mt-2" />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4">
                    <div class="text-caption grey--text text--darken-1 mb-1 font-weight-medium">
                      Overload threshold
                    </div>
                    <v-text-field
                      v-model.number="settings.overloadThreshold"
                      :rules="overloadRules"
                      type="number"
                      outlined
                      dense
                      suffix="%"
                      min="1"
                      max="150"
                      step="5"
                      style="max-width: 160px;" />
                  </v-col>
                  <v-col
                    cols="12"
                    md="4">
                    <div class="text-caption grey--text text--darken-1 mb-1 font-weight-medium">
                      Underused threshold
                    </div>
                    <v-text-field
                      v-model.number="settings.underUsedThreshold"
                      :rules="underUsedRules"
                      type="number"
                      outlined
                      dense
                      suffix="%"
                      min="1"
                      max="100"
                      step="5"
                      style="max-width: 160px;" />
                  </v-col>
                </v-row>
                <div class="d-flex justify-end mt-3">
                  <v-btn
                    depressed
                    color="primary"
                    small
                    :loading="savingSettings"
                    class="text-none"
                    @click="saveSettings">
                    Save settings
                  </v-btn>
                </div>
              </v-form>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import FactoryCapacityProvider from '@/resources/FactoryCapacityProvider'

const CapacityService = new FactoryCapacityProvider()

export default {
  name: 'RebalanceDialog',
  props: {
    value: Boolean,
    suggestion: { type: Array, default: () => [] }
  },
  data () {
    return {
      settingsExpanded: null,
      loading: false,
      savingSettings: false,
      settingsLoaded: false,
      horizonMode: 3,
      customHorizon: 12,
      settings: {
        planningHorizon: 3,
        overloadThreshold: 80,
        underUsedThreshold: 50
      }
    }
  },
  computed: {
    overloadedCount () {
      return this.suggestion.filter((e) => e.classification === 'Overloaded').length
    },
    underusedCount () {
      return this.suggestion.filter((e) => e.classification === 'Underused').length
    },
    proposedMovesCount () {
      return this.suggestion.reduce((sum, e) => sum + (e.suggestions ? e.suggestions.length : 0), 0)
    },
    overloadRules () {
      return [
        (v) => (v !== null && v !== '' && !Number.isNaN(v)) || 'Required',
        (v) => v > 0 || 'Must be a positive number',
        (v) => v > this.settings.underUsedThreshold || 'Must be greater than underused threshold'
      ]
    },
    underUsedRules () {
      return [
        (v) => (v !== null && v !== '' && !Number.isNaN(v)) || 'Required',
        (v) => v > 0 || 'Must be a positive number',
        (v) => v < this.settings.overloadThreshold || 'Must be less than overload threshold'
      ]
    }
  },
  watch: {
    value (val) {
      if (val) {
        this.loading = true
        this.loadSettings()
      }
    },
    horizonMode (val) {
      if (val !== 'custom') this.settings.planningHorizon = val
    },
    customHorizon (val) {
      if (this.horizonMode === 'custom') this.settings.planningHorizon = val
    }
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar'
    }),
    async loadSettings () {
      try {
        const { data } = await CapacityService.getRebalanceSettings()
        if (data) {
          this.settings.planningHorizon = data.planningHorizon
          this.settings.overloadThreshold = data.overloadThreshold
          this.settings.underUsedThreshold = data.underUsedThreshold
          if ([1, 3, 6].includes(data.planningHorizon)) {
            this.horizonMode = data.planningHorizon
          } else {
            this.horizonMode = 'custom'
            this.customHorizon = data.planningHorizon
          }
          this.settingsLoaded = true
        } else {
          this.settingsLoaded = false
        }
      } catch (e) {
        console.error('loadSettings', e?.message || e)
        this.setSnackbar({ value: true, message: `Failed to load rebalance settings: ${e?.message || e}`, type: 'error' })
      } finally {
        this.loading = false
      }
    },
    async saveSettings () {
      if (!this.$refs.settingsForm.validate()) return
      this.savingSettings = true
      try {
        await CapacityService.updateRebalanceSettings(this.settings)
        this.settingsLoaded = true
        this.$emit('saved')
        this.setSnackbar({ value: true, message: 'Rebalance settings saved', type: 'success' })
      } catch (e) {
        console.error('saveSettings', e?.message || e)
        this.setSnackbar({ value: true, message: `Error saving settings: ${e?.message || e}`, type: 'error' })
      } finally {
        this.savingSettings = false
      }
    }
  }
}
</script>
